import { shouldPolyfill as shouldPolyfillCanonicalLocales } from '@formatjs/intl-getcanonicallocales/should-polyfill';
import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill';
import { shouldPolyfill as shouldPolyfillDisplayNames } from '@formatjs/intl-displaynames/should-polyfill';
import { shouldPolyfill as shouldPolyfillPluralRules } from '@formatjs/intl-pluralrules/should-polyfill';
import { shouldPolyfill as shouldPolyfillListFormat } from '@formatjs/intl-listformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillNumberFormat } from '@formatjs/intl-numberformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from '@formatjs/intl-relativetimeformat/should-polyfill';
import { shouldPolyfill as shouldPolyfillDatetimeFormat } from '@formatjs/intl-datetimeformat/should-polyfill';

export const LOCALE_LOADING = 'LOCALE_LOADING' as const;
export const LOCALE = 'LOCALE' as const;
export const LOCALE_ERROR = 'LOCALE_ERROR' as const;

const validLocales = ['en-US' /* , 'en-TEST', 'fr-TEST' */];

const getLocale = () => {
  const locale = window.navigator.language;
  const language = locale.substring(0, 2);

  if (validLocales.includes(locale)) {
    return locale;
  }

  return (
    validLocales.find(validLocale => {
      return validLocale.includes(language);
    }) || 'en-US'
  );
};

const intlPolyfill = async () => {
  const locale = getLocale();
  const language = locale.substring(0, 2);

  const attemptCanonicalLocalesPolyfill = async () => {
    if (shouldPolyfillCanonicalLocales()) {
      await import('@formatjs/intl-getcanonicallocales/polyfill');
    }
  };

  const attemptLocalePolyfill = async () => {
    if (shouldPolyfillLocale()) {
      await import('@formatjs/intl-locale/polyfill');
    }
  };

  const attemptDisplayNamesPolyfill = async () => {
    if (shouldPolyfillDisplayNames()) {
      await import('@formatjs/intl-displaynames/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.DisplayNames.polyfilled) {
      if (language === 'en') {
        await import('@formatjs/intl-displaynames/locale-data/en');
      }
    }
  };

  const attemptPluralRulesPolyfill = async () => {
    if (shouldPolyfillPluralRules()) {
      await import('@formatjs/intl-pluralrules/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.PluralRules.polyfilled) {
      if (language === 'en') {
        await import('@formatjs/intl-pluralrules/locale-data/en');
      }
    }
  };

  const attemptListFormatPolyfill = async () => {
    if (shouldPolyfillListFormat()) {
      await import('@formatjs/intl-listformat/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.ListFormat.polyfilled) {
      if (language === 'en') {
        await import('@formatjs/intl-listformat/locale-data/en');
      }
    }
  };

  const attemptNumberFormatPolyfill = async () => {
    if (shouldPolyfillNumberFormat()) {
      await import('@formatjs/intl-numberformat/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.NumberFormat.polyfilled) {
      if (language === 'en') {
        await import('@formatjs/intl-numberformat/locale-data/en');
      }
    }
  };

  const attemptRelativeTimeFormatPolyfill = async () => {
    if (shouldPolyfillRelativeTimeFormat()) {
      await import('@formatjs/intl-relativetimeformat/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.RelativeTimeFormat.polyfilled) {
      if (language === 'en') {
        await import('@formatjs/intl-relativetimeformat/locale-data/en');
      }
    }
  };

  const attemptDateTimeFormatPolyfill = async () => {
    if (shouldPolyfillDatetimeFormat()) {
      await import('@formatjs/intl-datetimeformat/polyfill');
    }
    // @ts-ignore - polyfilled is added by @formatjs at runtime
    if (Intl.DateTimeFormat.polyfilled) {
      const dataPolyfills = [
        import('@formatjs/intl-datetimeformat/add-all-tz'),
      ];
      if (language === 'en') {
        dataPolyfills.push(
          import('@formatjs/intl-datetimeformat/locale-data/en'),
        );
      }
      await Promise.all(dataPolyfills);
    }
  };

  await attemptCanonicalLocalesPolyfill();
  await attemptLocalePolyfill();
  await Promise.all([
    attemptDisplayNamesPolyfill(),
    attemptPluralRulesPolyfill(),
    attemptListFormatPolyfill(),
  ]);
  await attemptNumberFormatPolyfill();
  await Promise.all([
    attemptRelativeTimeFormatPolyfill(),
    attemptDateTimeFormatPolyfill(),
  ]);
};

export const fetchLocale = () => {
  const locale = getLocale();
  let localeImport;
  if (locale === 'en-US') {
    localeImport = import('locales/en-US');
  }
  /* else if (locale === 'en-TEST') {
    localeImport = import('locales/en-TEST');
  } else if (locale === 'fr-TEST') {
    localeImport = import('locales/fr-TEST');
  } */
  const promises = [localeImport, intlPolyfill()];

  return {
    type: LOCALE,
    promise: Promise.all(promises),
    meta: locale,
  };
};
